// @ts-ignore
import {
  BrowserProvider,
  JsonRpcProvider,
  Contract,
  formatUnits,
  parseUnits,
  isAddress,
} from "ethers";
import { useCallback, useEffect, useState } from "react";

import Web3 from "web3";

import {
  KIND_ENERGY_ADDRESS,
  KIND_ENERGY_ABI,
  USDT_ADDRESS,
  TOKEN_ABI,
  KIND_TOKEN_ADDRESS,
} from "../contracts/contracts";

const ZERO = "0x0000000000000000000000000000000000000000";

const RPC_URLS = [
  "https://bsc-dataseed.binance.org",
  "https://bsc-dataseed1.defibit.io",
  "https://bsc-dataseed2.ninicoin.io",
  "https://bsc.publicnode.com",
  "https://bsc-mainnet.infura.io/v3/0f9ad8195c4e4acda7ee4a9cf5ee1ae0",
];

const CARD_PRICE = 50;
const TAX_PERCENT = 10;
const PER_CARD_TOTAL =
  CARD_PRICE + (CARD_PRICE * TAX_PERCENT) / 100;

function useContract() {
  const [address, setAddress] = useState(null);
  const [isConnected, setIsConnected] = useState(false);

  // =========================================================
  // WALLET
  // =========================================================

const getEthereum = useCallback(() => {
  if (typeof window === "undefined") {
    return null;
  }

  // Bitget Wallet browser extension
  if (window.bitkeep?.ethereum) {
    return window.bitkeep.ethereum;
  }

  // Fallback for other wallets
  if (window.ethereum) {
    return window.ethereum;
  }

  return null;
}, []);

  const connectWallet = useCallback(async () => {
    try {
      const ethereum = getEthereum();

      if (!ethereum) {
        throw new Error(
          "No Web3 wallet detected. Please install MetaMask or another compatible wallet."
        );
      }

      const accounts = await ethereum.request({
        method: "eth_requestAccounts",
      });

      if (accounts && accounts.length > 0) {
        setAddress(accounts[0]);
        setIsConnected(true);

        return accounts[0];
      }

      setAddress(null);
      setIsConnected(false);

      return null;
    } catch (err) {
      console.error("connectWallet error:", err);
      throw err;
    }
  }, [getEthereum]);

  const disconnectWallet = useCallback(() => {
    setAddress(null);
    setIsConnected(false);
  }, []);

  // =========================================================
  // CHECK EXISTING WALLET CONNECTION
  // =========================================================

  const checkWalletConnection = useCallback(async () => {
    try {
      const ethereum = getEthereum();

      if (!ethereum) {
        setAddress(null);
        setIsConnected(false);
        return;
      }

      const accounts = await ethereum.request({
        method: "eth_accounts",
      });

      if (accounts && accounts.length > 0) {
        setAddress(accounts[0]);
        setIsConnected(true);
      } else {
        setAddress(null);
        setIsConnected(false);
      }
    } catch (err) {
      console.error("checkWalletConnection error:", err);

      setAddress(null);
      setIsConnected(false);
    }
  }, [getEthereum]);

  // =========================================================
  // WALLET EVENTS
  // =========================================================

  useEffect(() => {
    const ethereum = getEthereum();

    if (!ethereum) {
      return;
    }

    checkWalletConnection();

    const handleAccountsChanged = (accounts) => {
      if (accounts && accounts.length > 0) {
        setAddress(accounts[0]);
        setIsConnected(true);
      } else {
        setAddress(null);
        setIsConnected(false);
      }
    };

    const handleChainChanged = () => {
      // Refresh wallet state after chain change
      checkWalletConnection();
    };

    ethereum.on?.("accountsChanged", handleAccountsChanged);
    ethereum.on?.("chainChanged", handleChainChanged);

    return () => {
      ethereum.removeListener?.(
        "accountsChanged",
        handleAccountsChanged
      );

      ethereum.removeListener?.(
        "chainChanged",
        handleChainChanged
      );
    };
  }, [getEthereum, checkWalletConnection]);

  // =========================================================
  // READ RPC
  // =========================================================

  const getWorkingProvider = useCallback(async () => {
    const timeout = 5000;

    for (const rpcUrl of RPC_URLS) {
      try {
        const provider = new JsonRpcProvider(rpcUrl);

        const timeoutPromise = new Promise((_, reject) => {
          setTimeout(() => {
            reject(new Error("RPC timeout"));
          }, timeout);
        });

        await Promise.race([
          provider.getNetwork(),
          timeoutPromise,
        ]);

        return provider;
      } catch (err) {
        console.warn(
          `RPC failed, trying next: ${rpcUrl}`,
          err
        );
      }
    }

    return new JsonRpcProvider(RPC_URLS[0]);
  }, []);

  const getReadContract = useCallback(async () => {
    const provider = await getWorkingProvider();

    return new Contract(
      KIND_ENERGY_ADDRESS,
      KIND_ENERGY_ABI,
      provider
    );
  }, [getWorkingProvider]);

  // =========================================================
  // WALLET PROVIDER
  // =========================================================

  const getProvider = useCallback(() => {
    const ethereum = getEthereum();

    if (!ethereum) {
      throw new Error(
        "Wallet not found. Please install MetaMask or another Web3 wallet."
      );
    }

    return new BrowserProvider(ethereum);
  }, [getEthereum]);

  // =========================================================
  // SIGNER
  // =========================================================

  const getSigner = useCallback(async () => {
    const provider = getProvider();

    return await provider.getSigner();
  }, [getProvider]);

  // =========================================================
  // MAIN CONTRACT
  // =========================================================

  const getContract = useCallback(async () => {
    const signer = await getSigner();

    return new Contract(
      KIND_ENERGY_ADDRESS,
      KIND_ENERGY_ABI,
      signer
    );
  }, [getSigner]);

  // =========================================================
  // USDT
  // =========================================================

  const getUSDT = useCallback(async () => {
    const signer = await getSigner();

    return new Contract(
      USDT_ADDRESS,
      TOKEN_ABI,
      signer
    );
  }, [getSigner]);

  // =========================================================
  // KIND
  // =========================================================

  const getKIND = useCallback(async () => {
    const signer = await getSigner();

    return new Contract(
      KIND_TOKEN_ADDRESS,
      TOKEN_ABI,
      signer
    );
  }, [getSigner]);

  // =========================================================
  // APPROVE USDT
  // =========================================================

  const approveUSDT = useCallback(
    async (amount) => {
      const usdt = await getUSDT();

      const value = parseUnits(
        amount.toString(),
        6
      );

      const tx = await usdt.approve(
        KIND_ENERGY_ADDRESS,
        value
      );

      await tx.wait();

      return tx.hash;
    },
    [getUSDT]
  );

  // =========================================================
  // ALLOWANCE
  // =========================================================

  const getAllowance = useCallback(
    async (user) => {
      if (!user) return "0";

      try {
        const usdt = await getUSDT();

        const allowance = await usdt.allowance(
          user,
          KIND_ENERGY_ADDRESS
        );

        return formatUnits(allowance, 6);
      } catch (err) {
        console.error("getAllowance error:", err);
        return "0";
      }
    },
    [getUSDT]
  );

  // =========================================================
  // BUY WITH USDT
  // =========================================================

  const buyWithUSDT = useCallback(
    async (count, ref) => {
      const contract = await getContract();
      const usdt = await getUSDT();

      const totalUsd =
        Number(count) * PER_CARD_TOTAL;

      const amount = parseUnits(
        totalUsd.toString(),
        18
      );

      const approveTx = await usdt.approve(
        KIND_ENERGY_ADDRESS,
        amount
      );

      await approveTx.wait();

      const safeRef =
        isAddress(ref) ? ref : ZERO;

      const tx = await contract.buy(
        count,
        safeRef
      );

      await tx.wait();

      return tx.hash;
    },
    [getContract, getUSDT]
  );

  // =========================================================
  // BUY WITH KIND
  // =========================================================

  const buyWithKIND = useCallback(
    async (count, ref) => {
      const contract = await getContract();
      const token = await getKIND();

      const totalUsd =
        Number(count) * PER_CARD_TOTAL;

      const price =
        await contract.getCurrentPrice(
          KIND_TOKEN_ADDRESS
        );

      const totalUsdBN =
        parseUnits(
          totalUsd.toString(),
          18
        );

      const kindAmount =
        (totalUsdBN * (10n ** 18n)) / price;

      const safetyBuffer =
        parseUnits("100", 18);

      const approveAmount =
        kindAmount + safetyBuffer;

      const approveTx =
        await token.approve(
          KIND_ENERGY_ADDRESS,
          approveAmount
        );

      await approveTx.wait();

      const safeRef =
        isAddress(ref) ? ref : ZERO;

      const tx =
        await contract.buyWithKind(
          count,
          safeRef
        );

      await tx.wait();

      return tx.hash;
    },
    [getContract, getKIND]
  );

  // =========================================================
  // BUY WITH BNB
  // =========================================================

  const buyWithBNB = useCallback(
    async (count, ref) => {
      const contract = await getContract();

      const totalUsd =
        Number(count) * 55;

      const wnative =
        await contract.wnative();

      const bnbPrice =
        await contract.getCurrentPrice(
          wnative
        );

      const usd =
        parseUnits(
          totalUsd.toString(),
          18
        );

      let bnbAmount =
        (usd * parseUnits("1", 18)) /
        bnbPrice;

      const buffer =
        (bnbAmount * 5n) / 100n;

      bnbAmount =
        bnbAmount + buffer;

      const safeRef =
        isAddress(ref) ? ref : ZERO;

      const tx =
        await contract.buyWithBNB(
          count,
          safeRef,
          {
            value: bnbAmount,
          }
        );

      await tx.wait();

      return tx.hash;
    },
    [getContract]
  );

  // =========================================================
  // WITHDRAW
  // =========================================================

const withdraw = useCallback(async () => {
  try {
    const ethereum = getEthereum();

    if (!ethereum) {
      throw new Error("Bitget Wallet not detected");
    }

    if (!address) {
      throw new Error("Wallet not connected");
    }

    const accounts = await ethereum.request({
      method: "eth_accounts",
    });

    if (!accounts?.length) {
      throw new Error("Wallet not connected");
    }

    const userAddress = accounts[0];

    // Get current chain
    const chainId = await ethereum.request({
      method: "eth_chainId",
    });

    console.log("Bitget chain:", chainId);
    console.log("Withdraw wallet:", userAddress);

    // Encode withdrawReward()
    const web3 = new Web3(ethereum);

    const contract = new web3.eth.Contract(
      KIND_ENERGY_ABI,
      KIND_ENERGY_ADDRESS
    );

    const data = contract.methods
      .withdrawReward()
      .encodeABI();

    console.log("Withdraw calldata:", data);

    // DIRECT BITGET WALLET TRANSACTION
    const txHash = await ethereum.request({
      method: "eth_sendTransaction",
      params: [
        {
          from: userAddress,
          to: KIND_ENERGY_ADDRESS,
          data: data,
        },
      ],
    });

    console.log("Withdrawal TX:", txHash);

    return txHash;
  } catch (err) {
    console.error("Bitget withdraw error:", err);
    throw err;
  }
}, [getEthereum, address]);
  // =========================================================
  // USER DASHBOARD
  // =========================================================

  const getUserDashboard =
    useCallback(
      async (user) => {
        if (!user) return null;

        try {
          const contract =
            await getReadContract();

          const d =
            await contract.getUserDashboard(
              user
            );

          return {
            totalPaidCards:
              Number(d[0]),

            activeCards:
              Number(d[1]),

            expiredCards:
              Number(d[2]),

            bonusCards:
              Number(d[3]),

            totalDeposits:
              formatUnits(d[4], 18),

            rank:
              Number(d[5]),

            currentRoiBps:
              Number(d[6]),

            nextRank:
              Number(d[7]),

            refsRemaining:
              Number(d[8]),

            energyWallet:
              formatUnits(d[9], 18),

            referralWallet:
              formatUnits(d[10], 18),

            totalWithdrawnUsd:
              formatUnits(d[11], 18),

            nextWithdrawTime:
              Number(d[12]),

            qualifiedRefs:
              Number(d[13]),
          };
        } catch (err) {
          console.error(
            "getUserDashboard error:",
            err
          );

          return null;
        }
      },
      [getReadContract]
    );

  // =========================================================
  // CLAIMABLE
  // =========================================================

  const getClaimable =
    useCallback(
      async (user) => {
        if (!user) {
          return {
            energy: "0",
            referral: "0",
          };
        }

        try {
          const contract =
            await getReadContract();

          const [
            energy,
            referral,
          ] =
            await contract.getUserClaimable(
              user
            );

          return {
            energy:
              formatUnits(
                energy,
                18
              ),

            referral:
              formatUnits(
                referral,
                18
              ),
          };
        } catch (err) {
          console.error(
            "getClaimable error:",
            err
          );

          return {
            energy: "0",
            referral: "0",
          };
        }
      },
      [getReadContract]
    );

  // =========================================================
  // GLOBAL STATS
  // =========================================================

  const getGlobalStats =
    useCallback(async () => {
      try {
        const contract =
          await getReadContract();

        const g =
          await contract.getGlobalStats();

        return {
          users:
            Number(g[0]),

          totalDeposits:
            formatUnits(g[1], 18),

          cardsSold:
            Number(g[2]),

          tokenPaid:
            formatUnits(g[3], 18),

          rewardUsdAdded:
            formatUnits(g[4], 18),

          rewardUsdPaid:
            formatUnits(g[5], 18),

          poolBalance:
            formatUnits(g[6], 18),
        };
      } catch (err) {
        console.error(
          "getGlobalStats error:",
          err
        );

        return {
          users: 0,
          totalDeposits: "0",
          cardsSold: 0,
          tokenPaid: "0",
          rewardUsdAdded: "0",
          rewardUsdPaid: "0",
          poolBalance: "0",
        };
      }
    }, [getReadContract]);

  // =========================================================
  // ADMIN
  // =========================================================

  const isAdmin =
    useCallback(async () => {
      if (!address) return false;

      try {
        const contract =
          await getContract();

        const owner =
          await contract.owner();

        return (
          owner.toLowerCase() ===
          address.toLowerCase()
        );
      } catch (err) {
        console.error(
          "isAdmin error:",
          err
        );

        return false;
      }
    }, [getContract, address]);

  const setWallets = async (
    trade,
    dev,
    marketing,
    burn,
    reward
  ) => {
    const contract =
      await getContract();

    const tx =
      await contract.setWallets(
        trade,
        dev,
        marketing,
        burn,
        reward
      );

    await tx.wait();

    return tx.hash;
  };

  const setCardPrice =
    async (price) => {
      const contract =
        await getContract();

      const tx =
        await contract.setCardPrice(
          parseUnits(
            price.toString(),
            18
          )
        );

      await tx.wait();

      return tx.hash;
    };

  const setTax =
    async (tax) => {
      const contract =
        await getContract();

      const tx =
        await contract.setTax(tax);

      await tx.wait();

      return tx.hash;
    };

  const setWithdrawLimit =
    async (limit) => {
      const contract =
        await getContract();

      const tx =
        await contract.setWithdrawLimit(
          parseUnits(
            limit.toString(),
            18
          )
        );

      await tx.wait();

      return tx.hash;
    };

  const setWithdrawCooldown =
    async (seconds) => {
      const contract =
        await getContract();

      const tx =
        await contract.setWithdrawCooldown(
          seconds
        );

      await tx.wait();

      return tx.hash;
    };

  const setCardDuration =
    async (seconds) => {
      const contract =
        await getContract();

      const tx =
        await contract.setCardDuration(
          seconds
        );

      await tx.wait();

      return tx.hash;
    };

  const setBonusDivider =
    async (divider) => {
      const contract =
        await getContract();

      const tx =
        await contract.setBonusDivider(
          divider
        );

      await tx.wait();

      return tx.hash;
    };

  const setMinQualifiedCardsForReferral =
    async (count) => {
      const contract =
        await getContract();

      const tx =
        await contract.setMinQualifiedCardsForReferral(
          count
        );

      await tx.wait();

      return tx.hash;
    };

  const setBasePercents =
    async (
      dev,
      reward,
      burn,
      marketing
    ) => {
      const contract =
        await getContract();

      const tx =
        await contract.setBasePercents(
          dev,
          reward,
          burn,
          marketing
        );

      await tx.wait();

      return tx.hash;
    };

  const setReferralPercents =
    async (vals) => {
      const contract =
        await getContract();

      const tx =
        await contract.setReferralPercents(
          vals
        );

      await tx.wait();

      return tx.hash;
    };

  const setRoiRatesBps =
    async (vals) => {
      const contract =
        await getContract();

      const tx =
        await contract.setRoiRatesBps(
          vals
        );

      await tx.wait();

      return tx.hash;
    };

  const setRankThresholds =
    async (vals) => {
      const contract =
        await getContract();

      const tx =
        await contract.setRankThresholds(
          vals
        );

      await tx.wait();

      return tx.hash;
    };

  const setEarningsPause =
    async (paused) => {
      const contract =
        await getContract();

      const tx =
        await contract.setEarningsPause(
          paused
        );

      await tx.wait();

      return tx.hash;
    };

  const manualTopUp =
    async (amount) => {
      const contract =
        await getContract();

      const token =
        await contract.defaultPayoutToken();

      const value =
        parseUnits(
          amount.toString(),
          18
        );

      const signer =
        await getSigner();

      const erc20 =
        new Contract(
          token,
          TOKEN_ABI,
          signer
        );

      const approveTx =
        await erc20.approve(
          KIND_ENERGY_ADDRESS,
          value
        );

      await approveTx.wait();

      const tx =
        await contract.manualTopUp(
          value
        );

      await tx.wait();

      return tx.hash;
    };

  const adminSettleAllUsers =
    async () => {
      const contract =
        await getContract();

      const tx =
        await contract.adminSettleAllUsers();

      await tx.wait();

      return tx.hash;
    };

  const adminSettleUserRange =
    async (start, end) => {
      const contract =
        await getContract();

      const tx =
        await contract.adminSettleAllUsersRange(
          start,
          end
        );

      await tx.wait();

      return tx.hash;
    };

  const setManualPrice =
    async (
      token,
      price
    ) => {
      const contract =
        await getContract();

      const tx =
        await contract.setManualPrice(
          token,
          parseUnits(
            price.toString(),
            18
          )
        );

      await tx.wait();

      return tx.hash;
    };

  const setRouter =
    async (router) => {
      const contract =
        await getContract();

      const tx =
        await contract.setRouter(
          router
        );

      await tx.wait();

      return tx.hash;
    };

  const setDefaultPayoutToken =
    async (token) => {
      const contract =
        await getContract();

      const tx =
        await contract.setDefaultPayoutToken(
          token
        );

      await tx.wait();

      return tx.hash;
    };

  const setApprovedPayoutToken =
    async (
      token,
      approved
    ) => {
      const contract =
        await getContract();

      const tx =
        await contract.setApprovedPayoutToken(
          token,
          approved
        );

      await tx.wait();

      return tx.hash;
    };

  const setKindToken =
    async (token) => {
      const contract =
        await getContract();

      const tx =
        await contract.setKindToken(
          token
        );

      await tx.wait();

      return tx.hash;
    };

  const setWNative =
    async (addr) => {
      const contract =
        await getContract();

      const tx =
        await contract.setWNative(
          addr
        );

      await tx.wait();

      return tx.hash;
    };

  const setPairManually =
    async (
      token,
      pair
    ) => {
      const contract =
        await getContract();

      const tx =
        await contract.setPairManually(
          token,
          pair
        );

      await tx.wait();

      return tx.hash;
    };

  // =========================================================
  // REWARD POOL
  // =========================================================

  const getRewardPoolRemainingUsd =
    useCallback(async () => {
      try {
        const contract =
          await getReadContract();

        const balance =
          await contract.getRewardPoolRemainingUsd();

        return Math.floor(
          Number(
            formatUnits(
              balance,
              18
            )
          )
        ).toString();
      } catch (err) {
        console.error(
          "Error fetching pool:",
          err
        );

        return "0";
      }
    }, [getReadContract]);

  // =========================================================
  // BATCH COUNT
  // =========================================================

  const getBatchCount =
    useCallback(
      async (user) => {
        if (!user) return 0;

        try {
          const contract =
            await getReadContract();

          const count =
            await contract.getBatchCount(
              user
            );

          return Number(
            count.toString()
          );
        } catch (err) {
          console.error(
            "getBatchCount error:",
            err
          );

          return 0;
        }
      },
      [getReadContract]
    );

  // =========================================================
  // GET BATCH
  // =========================================================

  const getBatch =
    useCallback(
      async (
        userAddr,
        index
      ) => {
        if (!userAddr) {
          throw new Error(
            "User address required"
          );
        }

        try {
          const contract =
            await getReadContract();

          const batch =
            await contract.getBatch(
              userAddr,
              index
            );

          const toNumberSafe =
            (v) => {
              if (
                v &&
                typeof v.toNumber ===
                  "function"
              ) {
                return v.toNumber();
              }

              return Number(v) || 0;
            };

          const toStringSafe =
            (v) => {
              if (
                v &&
                typeof v.toString ===
                  "function"
              ) {
                return v.toString();
              }

              return v
                ? String(v)
                : "0";
            };

          return {
            totalCards:
              toStringSafe(batch[0]),

            paidCards:
              toStringSafe(batch[1]),

            bonusCards:
              toStringSafe(batch[2]),

            userLevelAtStart:
              toNumberSafe(batch[3]),

            startTime:
              toNumberSafe(batch[4]),

            lastCheckpoint:
              toNumberSafe(batch[5]),

            active:
              batch[6],
          };
        } catch (err) {
          console.error(
            "getBatch error:",
            err
          );

          return {
            totalCards: "0",
            paidCards: "0",
            bonusCards: "0",
            userLevelAtStart: 0,
            startTime: 0,
            lastCheckpoint: 0,
            active: false,
          };
        }
      },
      [getReadContract]
    );

  // =========================================================
  // ALL BATCHES
  // =========================================================

  const getAllBatches =
    useCallback(
      async (userAddr) => {
        if (!userAddr) return [];

        try {
          const contract =
            await getReadContract();

          const count =
            await contract.getBatchCount(
              userAddr
            );

          const batches = [];

          for (
            let i = 0;
            i < Number(count);
            i++
          ) {
            const batch =
              await contract.getBatch(
                userAddr,
                i
              );

            batches.push({
              totalCards:
                batch[0].toString(),

              paidCards:
                batch[1].toString(),

              bonusCards:
                batch[2].toString(),

              userLevelAtStart:
                Number(batch[3]),

              startTime:
                Number(batch[4]),

              lastCheckpoint:
                Number(batch[5]),

              active:
                batch[6],
            });
          }

          return batches;
        } catch (err) {
          console.error(
            "getAllBatches error:",
            err
          );

          return [];
        }
      },
      [getReadContract]
    );

  // =========================================================
  // USDT BALANCE
  // =========================================================

  const getUSDTBalance =
    useCallback(async () => {
      if (!address) return "0";

      try {
        const contract =
          await getReadContract();

        const usdt =
          new Contract(
            USDT_ADDRESS,
            TOKEN_ABI,
            contract.runner
          );

        const balance =
          await usdt.balanceOf(
            address
          );

        return formatUnits(
          balance,
          18
        );
      } catch (err) {
        console.error(
          "getUSDTBalance error:",
          err
        );

        return "0";
      }
    }, [
      getReadContract,
      address,
    ]);

  // =========================================================
  // KIND BALANCE
  // =========================================================

  const getKINDBalance =
    useCallback(async () => {
      if (!address) return "0";

      try {
        const contract =
          await getReadContract();

        const kind =
          new Contract(
            KIND_TOKEN_ADDRESS,
            TOKEN_ABI,
            contract.runner
          );

        const balance =
          await kind.balanceOf(
            address
          );

        return formatUnits(
          balance,
          18
        );
      } catch (err) {
        console.error(
          "getKINDBalance error:",
          err
        );

        return "0";
      }
    }, [
      getReadContract,
      address,
    ]);

  // =========================================================
  // BNB BALANCE
  // =========================================================

  const getBNBBalance =
    useCallback(async () => {
      if (!address) return "0";

      try {
        const provider =
          getProvider();

        const balance =
          await provider.getBalance(
            address
          );

        return formatUnits(
          balance,
          18
        );
      } catch (err) {
        console.error(
          "getBNBBalance error:",
          err
        );

        return "0";
      }
    }, [
      getProvider,
      address,
    ]);

  // =========================================================
  // BURN STATS
  // =========================================================

  const getBurnStats =
    useCallback(async () => {
      try {
        const contract =
          await getReadContract();

        const data =
          await contract.getBurnOverview();

        return {
          pendingUsd:
            Number(
              formatUnits(
                data[0],
                18
              )
            ),

          burnedUsd:
            Number(
              formatUnits(
                data[1],
                18
              )
            ),

          burnedKind:
            Number(
              formatUnits(
                data[2],
                18
              )
            ),
        };
      } catch (err) {
        console.error(
          "Burn stats error:",
          err
        );

        return {
          pendingUsd: 0,
          burnedUsd: 0,
          burnedKind: 0,
        };
      }
    }, [getReadContract]);

  // =========================================================
  // REFERRAL STATS
  // =========================================================

  const getUserReferralStats =
    async (userAddress) => {
      try {
        const contract =
          await getReadContract();

        const totalRefs =
          await contract.totalDirectReferrals(
            userAddress
          );

        const volume =
          await contract.volumes(
            userAddress
          );

        return {
          totalReferrals:
            Number(totalRefs),

          teamVolume:
            Number(
              volume.teamVolume
            ) / 1e18,

          directVolume:
            Number(
              volume.directVolume
            ) / 1e18,
        };
      } catch (err) {
        console.error(
          "Referral Stats Error:",
          err
        );

        return {
          totalReferrals: 0,
          teamVolume: 0,
          directVolume: 0,
        };
      }
    };

  // =========================================================
  // PAUSE
  // =========================================================

  const setPauseState =
    async (
      earnings,
      buy,
      withdraw
    ) => {
      const contract =
        await getContract();

      const tx =
        await contract.setPauseState(
          earnings,
          buy,
          withdraw
        );

      await tx.wait();

      return tx.hash;
    };

  // =========================================================
  // REFERRAL SETTINGS
  // =========================================================

  const setMaxReferralLevels =
    async (levels) => {
      const contract =
        await getContract();

      const tx =
        await contract.setMaxReferralLevels(
          levels
        );

      await tx.wait();

      return tx.hash;
    };

  const setDirectReferralPercent =
    async (percent) => {
      const contract =
        await getContract();

      const tx =
        await contract.setDirectReferralPercent(
          percent
        );

      await tx.wait();

      return tx.hash;
    };

  // =========================================================
  // BURN ADMIN
  // =========================================================

  const setBurnThreshold =
    async (usd) => {
      const contract =
        await getContract();

      const tx =
        await contract.setBurnThreshold(
          parseUnits(
            usd.toString(),
            18
          )
        );

      await tx.wait();

      return tx.hash;
    };

  const executeFullBurn =
    async () => {
      const contract =
        await getContract();

      const tx =
        await contract.executeFullBurn();

      await tx.wait();

      return tx.hash;
    };

  // =========================================================
  // RECOVER
  // =========================================================

  const recoverERC20 =
    async (
      token,
      amount
    ) => {
      const contract =
        await getContract();

      const tx =
        await contract.recoverERC20(
          token,
          parseUnits(
            amount.toString(),
            18
          )
        );

      await tx.wait();

      return tx.hash;
    };

  const recoverBNB =
    async (amount) => {
      const contract =
        await getContract();

      const tx =
        await contract.recoverBNB(
          parseUnits(
            amount.toString(),
            18
          )
        );

      await tx.wait();

      return tx.hash;
    };

  // =========================================================
  // OWNERSHIP
  // =========================================================

  const transferOwnership =
    async (newOwner) => {
      const contract =
        await getContract();

      const tx =
        await contract.transferOwnership(
          newOwner
        );

      await tx.wait();

      return tx.hash;
    };

  const renounceOwnership =
    async () => {
      const contract =
        await getContract();

      const tx =
        await contract.renounceOwnership();

      await tx.wait();

      return tx.hash;
    };

  // =========================================================
  // RETURN
  // =========================================================

  return {
    // wallet
    address,
    isConnected,
    connectWallet,
    disconnectWallet,

    // user
    approveUSDT,
    getAllowance,
    getBurnStats,
    withdraw,
    getRewardPoolRemainingUsd,
    getBatchCount,
    getAllBatches,
    recoverERC20,

    // reads
    getUserDashboard,
    getClaimable,
    getGlobalStats,

    // buys
    buyWithUSDT,
    buyWithKIND,
    buyWithBNB,

    // referral
    getUserReferralStats,

    // balances
    getUSDTBalance,
    getKINDBalance,
    getBNBBalance,

    // pause
    setPauseState,
    setMaxReferralLevels,

    // burn
    setBurnThreshold,
    executeFullBurn,

    // recovery
    recoverBNB,

    // admin
    isAdmin,

    setWallets,
    setCardPrice,
    setTax,
    setWithdrawLimit,
    setWithdrawCooldown,
    setCardDuration,
    setBonusDivider,
    setMinQualifiedCardsForReferral,
    setBasePercents,
    setReferralPercents,
    setRoiRatesBps,
    setRankThresholds,
    setEarningsPause,
    manualTopUp,
    adminSettleAllUsers,
    adminSettleUserRange,
    setManualPrice,
    setRouter,
    setDefaultPayoutToken,
    setApprovedPayoutToken,
    setKindToken,
    setWNative,
    setPairManually,

    getBatch,

    setDirectReferralPercent,

    renounceOwnership,
    transferOwnership,
  };
}

export default useContract;